/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useRef } from "react";
import { useApolloClient, useMutation } from "@apollo/react-hooks";
import { Dimmer, Loader } from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import { authCheckCallApi } from "../../Authenticator/utils";
import { UpdateUserMutation, getS3PresignedUrl } from "../../../graphql/user";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import userImage from "../../Icon/User.svg";
import { setMenuProfileImage } from "../../Menu/Menu";
import { Ico } from "../../Icon/Icon";
import ConfirmationDialog from "../../ConfirmationDialog";
import { useTranslation } from "react-i18next";

const maxPixelSize = 96;

interface ChangeImageProps {
  id: string | undefined;
  readOnly: boolean;
  imageUrl: string | undefined;
  onImageChanged: any;
}

const ChangeImage: React.FC<ChangeImageProps> = ({
  id,
  readOnly,
  imageUrl,
  onImageChanged,
}) => {
  const client = useApolloClient();
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const inputRef: any = useRef(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation("common");
  const [updateUser] = useMutation(UpdateUserMutation, {
    onError: () => {
      !message &&
        setMessage({
          title: t("update_image"),
          message: t("update_image_error"),
        });
      setLoading(false);
    },
    onCompleted: () => {
      onImageChanged();
      setLoading(false);
    },
  });

  const updateImageUrl = (imageUrl: string | null) => {
    setLoading(true);
    authCheckCallApi(updateUser, {
      variables: {
        user: {
          id: id,
          imageUrl: imageUrl,
        },
      },
    });
    setMenuProfileImage(imageUrl || "default");
  };

  const uploadImage = async (
    data: string,
    uploadUrl: string,
    imageUrl: string
  ) => {
    await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "image/jpeg",
        "Cache-Control": "max-age=86400",
      },
      body: data,
    })
      .then((response) => {
        if (!response.ok) {
          setMessage({
            title: t("upload_failed"),
            message: t("server_error"),
          });
        } else {
          updateImageUrl(imageUrl);
        }
      })
      .catch((e) => {
        setMessage({
          title: t("upload_failed"),
          message: e.message,
        });
      })
      .finally(() => {
        setLoading(false);
        inputRef.current.value = "";
      });
  };

  const getUploadUrl = (fileData: any) => {
    authCheckCallApi(() => {
      client
        .query({
          fetchPolicy: "no-cache",
          query: getS3PresignedUrl,
          variables: { uploadType: "user", mimeType: "image/jpeg" },
        })
        .then((result) => {
          if (
            !result.data ||
            !result.data.getS3PresignedUrl ||
            !result.data.getS3PresignedUrl.uploadUrl ||
            !result.data.getS3PresignedUrl.downloadUrl
          ) {
            setMessage({
              title: t("upload_failed"),
              message: t("upload_error"),
            });
            setLoading(false);
            return;
          }
          uploadImage(
            fileData,
            result.data.getS3PresignedUrl.uploadUrl,
            result.data.getS3PresignedUrl.downloadUrl
          );
        })
        .catch((e) => {
          setMessage({
            title: t("upload_failed"),
            message: e.message,
          });
          setLoading(false);
        });
    });
  };

  const onImageResized = (dataUrl: string) => {
    const req = new XMLHttpRequest();
    req.open("GET", dataUrl);
    req.responseType = "arraybuffer";
    req.onload = function fileLoaded(e: any) {
      getUploadUrl(e.target.response);
    };
    req.send();
  };

  function resizeImage(image: any, mimeType: string) {
    const canvas = document.createElement("canvas");
    let width = image.width;
    let height = image.height;

    if (width > height) {
      if (width > maxPixelSize) {
        height = Math.round((height * maxPixelSize) / width);
        width = maxPixelSize;
      }
    } else {
      if (height > maxPixelSize) {
        width = Math.round((width * maxPixelSize) / height);
        height = maxPixelSize;
      }
    }

    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) {
      imageLoadError();
      return;
    }

    context.globalAlpha = 1;
    context.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL(mimeType);
  }

  const imageLoadError = () => {
    setMessage({
      title: t("failed_image_load"),
      message: t("select_different_file"),
    });
    setLoading(false);
  };

  const onImageLoaded = (dataUrl: any, mimeType: string) => {
    const image = new Image();
    image.src = dataUrl;
    image.onload = () => {
      const scaledDataUrl = resizeImage(image, mimeType);
      if (!scaledDataUrl) {
        imageLoadError();
        return;
      }
      onImageResized(scaledDataUrl);
    };
  };

  const loadImage = () => {
    if (
      !inputRef ||
      !inputRef.current ||
      !inputRef.current.files ||
      inputRef.current.files.length !== 1
    ) {
      imageLoadError();
      return;
    }

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target && e.target.result) {
          onImageLoaded(e.target.result, inputRef.current.files[0].type);
        } else {
          imageLoadError();
        }
      };
      reader.onerror = () => {
        imageLoadError();
      };

      reader.readAsDataURL(inputRef.current.files[0]);
    } catch (e) {
      imageLoadError();
    }
  };

  return (
    <div className={styles.changeImage}>
      <div>
        <Dimmer active={loading}>
          <Loader size="large" active={loading}></Loader>
        </Dimmer>

        <img
          src={imageUrl || userImage}
          alt="Profile"
          className={styles.profileImage}
          width={imageUrl ? "" : "96"}
          height={imageUrl ? "" : "96"}
        />
      </div>

      {!readOnly && (
        <label
          htmlFor="imageInput"
          className={`${styles.myAccountLink} ${styles.changeImageLink}`}
        >
          <Ico
            name="upload"
            style={{ marginRight: "7px", verticalAlign: "text-bottom" }}
            className={styles.ico}
          />
          {t("upload_image")}
        </label>
      )}

      {imageUrl && !readOnly && (
        <span
          className={`${styles.myAccountLink} ${styles.changeImageLink}`}
          onClick={() => {
            updateImageUrl(null);
          }}
        >
          <Ico
            name="cancel"
            style={{ marginRight: "5px", verticalAlign: "text-bottom" }}
            className={styles.ico}
          />
          {t("remove")}
        </span>
      )}

      <input
        id="imageInput"
        type="file"
        ref={inputRef}
        accept="image/png,image/x-png,image/gif,image/jpeg"
        onChange={loadImage}
      />

      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => {
            setMessage(null);
          }}
        />
      )}
    </div>
  );
};

export default ChangeImage;
